import { useMemo, useState } from 'react';
import { Loader2, Plus, Crown, ShieldCheck } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { queryKeys } from '@/lib/query-keys';
import {
  useAddPartnerAdmin,
  useRemovePartnerAdmin,
} from '@/hooks/useEnterpriseSsoActions';
import { useSuperAdmin } from '@/hooks/useSuperAdmin';
import { useWheelScroll } from '@/hooks/useWheelScroll';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { useToast } from '@/hooks/use-toast';

interface PartnerAdminListProps {
  partnerId: string;
}

/**
 * Two-query model: fetch the admin rows from enterprise_partner_admins
 * (RLS-gated by M003 caller_can_manage_partner), then fetch the user
 * details (id, email, first_name, last_name) via the M010 SECURITY DEFINER
 * `enterprise_sso_users_by_ids` RPC. We don't embed via PostgREST because
 * `public.users` has no `email` column — email lives in `auth.users` and
 * is exposed only through the staff-only RPC.
 */
interface AdminUserEmbed {
  id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
}

interface AdminRow {
  user_id: string;
  is_workspace_owner: boolean;
  created_at: string;
  users: AdminUserEmbed | null;
}

interface UserSearchRow {
  id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
}

function displayName(user: AdminUserEmbed | UserSearchRow | null): string {
  if (!user) return 'Unknown user';
  const fullName = [user.first_name, user.last_name]
    .filter((v): v is string => Boolean(v && v.trim()))
    .join(' ')
    .trim();
  if (fullName) return fullName;
  if (user.email) return user.email;
  return user.id;
}

function parseHardErrorMessage(message: string): string | null {
  if (message.includes('cannot_remove_last_admin')) {
    return 'Cannot remove the last admin. Add another admin first.';
  }
  if (message.includes('transfer_ownership_first')) {
    return 'This user is the sole workspace owner of one or more workspaces. Transfer ownership before removing them.';
  }
  if (message.includes('workspace_owner_flip_requires_staff')) {
    return 'Only staff can change the workspace owner.';
  }
  return null;
}

export function PartnerAdminList({
  partnerId,
}: PartnerAdminListProps): JSX.Element {
  const { toast } = useToast();
  const { isSuperAdmin } = useSuperAdmin();
  const addAdmin = useAddPartnerAdmin();
  const removeAdmin = useRemovePartnerAdmin();
  // Both pickers are portalled Popovers inside the admin Sheet (modal): wheel needs this.
  const transferListRef = useWheelScroll();
  const addListRef = useWheelScroll();

  const [showAddPicker, setShowAddPicker] = useState(false);
  const [addSearch, setAddSearch] = useState('');
  const [showTransferPicker, setShowTransferPicker] = useState(false);
  const [confirmRemoveTarget, setConfirmRemoveTarget] =
    useState<AdminRow | null>(null);

  // Step 1: admin grants (RLS-gated by M003 → caller_can_manage_partner).
  const adminGrantsQuery = useQuery({
    queryKey: [...queryKeys.enterprisePartners.admins(partnerId), 'grants'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('enterprise_partner_admins')
        .select('user_id, is_workspace_owner, created_at')
        .eq('partner_id', partnerId);
      if (error) throw error;
      return (data ?? []) as Array<{
        user_id: string;
        is_workspace_owner: boolean;
        created_at: string;
      }>;
    },
  });

  // Step 2: user details for those admin user_ids (M010 staff-only RPC).
  const grants = adminGrantsQuery.data ?? [];
  const grantUserIds = useMemo(() => grants.map((g) => g.user_id), [grants]);
  const usersByIdsQuery = useQuery({
    queryKey: [
      ...queryKeys.enterprisePartners.admins(partnerId),
      'users',
      [...grantUserIds].sort().join(','),
    ],
    queryFn: async (): Promise<AdminUserEmbed[]> => {
      if (grantUserIds.length === 0) return [];
      const { data, error } = await supabase.rpc('enterprise_sso_users_by_ids', {
        p_ids: grantUserIds,
      });
      if (error) throw error;
      return (data ?? []) as AdminUserEmbed[];
    },
    enabled: grantUserIds.length > 0,
  });

  // Compose: admin grant + user details into AdminRow shape.
  const adminsQuery = {
    isLoading: adminGrantsQuery.isLoading || usersByIdsQuery.isLoading,
    error: adminGrantsQuery.error ?? usersByIdsQuery.error,
    data: useMemo<AdminRow[]>(() => {
      const userMap = new Map(
        (usersByIdsQuery.data ?? []).map((u) => [u.id, u])
      );
      return grants.map((g) => ({
        user_id: g.user_id,
        is_workspace_owner: g.is_workspace_owner,
        created_at: g.created_at,
        users: userMap.get(g.user_id) ?? null,
      }));
    }, [grants, usersByIdsQuery.data]),
  };

  const admins = adminsQuery.data;
  const adminUserIds = useMemo(
    () => new Set(admins.map((a) => a.user_id)),
    [admins]
  );

  // Search users (excluding existing admins) for the add-admin picker.
  // Only runs when the picker is open and there's a search term.
  // M009 staff-only RPC: joins public.users + auth.users so we can search
  // by email. Direct query against public.users would fail — that table
  // has no email column. Returns empty for non-staff callers.
  const userSearchQuery = useQuery({
    queryKey: ['enterprisePartners', 'userSearch', partnerId, addSearch],
    queryFn: async (): Promise<UserSearchRow[]> => {
      const term = addSearch.trim();
      if (!term) return [];
      const { data, error } = await supabase.rpc('enterprise_sso_user_search', {
        p_q: term,
        p_limit: 20,
      });
      if (error) throw error;
      return (data ?? []) as UserSearchRow[];
    },
    enabled: showAddPicker && addSearch.trim().length > 0,
  });

  const filteredSearchResults = useMemo(
    () =>
      (userSearchQuery.data ?? []).filter((u) => !adminUserIds.has(u.id)),
    [userSearchQuery.data, adminUserIds]
  );

  const handleMutationError = (err: unknown, fallbackPrefix = 'Failed') => {
    const message = err instanceof Error ? err.message : String(err);
    const parsed = parseHardErrorMessage(message);
    toast({
      title: parsed ? 'Action blocked' : 'Error',
      description: parsed ?? `${fallbackPrefix}: ${message}`,
      variant: 'destructive',
    });
  };

  const handleAddAdmin = async (user: UserSearchRow) => {
    try {
      await addAdmin.mutateAsync({
        partner_id: partnerId,
        user_id: user.id,
        is_workspace_owner: false,
      });
      toast({ description: `Added ${displayName(user)} as admin.` });
      setShowAddPicker(false);
      setAddSearch('');
    } catch (err) {
      handleMutationError(err, 'Failed to add admin');
    }
  };

  const handleConfirmRemove = async () => {
    if (!confirmRemoveTarget) return;
    const target = confirmRemoveTarget;
    try {
      await removeAdmin.mutateAsync({
        partner_id: partnerId,
        user_id: target.user_id,
      });
      toast({ description: `Removed ${displayName(target.users)}.` });
      setConfirmRemoveTarget(null);
    } catch (err) {
      handleMutationError(err, 'Failed to remove admin');
      setConfirmRemoveTarget(null);
    }
  };

  const handleTransferOwnership = async (target: AdminRow) => {
    try {
      await addAdmin.mutateAsync({
        partner_id: partnerId,
        user_id: target.user_id,
        is_workspace_owner: true,
      });
      toast({
        description: `Transferred workspace ownership to ${displayName(
          target.users
        )}.`,
      });
      setShowTransferPicker(false);
    } catch (err) {
      handleMutationError(err, 'Failed to transfer ownership');
    }
  };

  if (adminsQuery.isLoading) {
    return (
      <div className="space-y-2">
        <div className="h-10 animate-pulse rounded bg-muted" />
        <div className="h-10 animate-pulse rounded bg-muted" />
        <div className="h-10 animate-pulse rounded bg-muted" />
      </div>
    );
  }

  if (adminsQuery.error) {
    return (
      <div className="space-y-3 rounded border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
        <p>
          Failed to load partner admins:{' '}
          {adminsQuery.error instanceof Error
            ? adminsQuery.error.message
            : 'Unknown error'}
        </p>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            void adminGrantsQuery.refetch();
            void usersByIdsQuery.refetch();
          }}
        >
          Retry
        </Button>
      </div>
    );
  }

  const transferEligible = admins.filter((a) => !a.is_workspace_owner);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Partner admins</h3>
        <div className="flex items-center gap-2">
          {isSuperAdmin && transferEligible.length > 0 && (
            <Popover
              open={showTransferPicker}
              onOpenChange={setShowTransferPicker}
            >
              <PopoverTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={addAdmin.isPending}
                >
                  <Crown className="mr-2 h-4 w-4" />
                  Transfer ownership
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0" align="end">
                <Command>
                  <CommandList ref={transferListRef}>
                    <CommandEmpty>No eligible admins.</CommandEmpty>
                    <CommandGroup heading="Promote to workspace owner">
                      {transferEligible.map((row) => (
                        <CommandItem
                          key={row.user_id}
                          value={`${displayName(row.users)} ${
                            row.users?.email ?? ''
                          }`}
                          onSelect={() => {
                            void handleTransferOwnership(row);
                          }}
                          disabled={addAdmin.isPending}
                        >
                          <ShieldCheck className="mr-2 h-4 w-4 text-muted-foreground" />
                          <div className="flex flex-col">
                            <span className="text-sm">
                              {displayName(row.users)}
                            </span>
                            {row.users?.email && (
                              <span className="text-xs text-muted-foreground">
                                {row.users.email}
                              </span>
                            )}
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          )}
          <Popover open={showAddPicker} onOpenChange={setShowAddPicker}>
            <PopoverTrigger asChild>
              <Button size="sm" disabled={addAdmin.isPending}>
                <Plus className="mr-2 h-4 w-4" />
                Add admin
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-2" align="end">
              <div className="space-y-2">
                <Input
                  autoFocus
                  placeholder="Search by name or email…"
                  value={addSearch}
                  onChange={(e) => setAddSearch(e.target.value)}
                />
                <div
                  ref={addListRef}
                  className="max-h-64 overflow-y-auto rounded border"
                >
                  {addSearch.trim().length === 0 && (
                    <p className="p-3 text-xs text-muted-foreground">
                      Type a name or email to search.
                    </p>
                  )}
                  {addSearch.trim().length > 0 && userSearchQuery.isFetching && (
                    <div className="flex items-center justify-center p-3 text-xs text-muted-foreground">
                      <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                      Searching…
                    </div>
                  )}
                  {addSearch.trim().length > 0 &&
                    !userSearchQuery.isFetching &&
                    filteredSearchResults.length === 0 && (
                      <p className="p-3 text-xs text-muted-foreground">
                        No matching users.
                      </p>
                    )}
                  {filteredSearchResults.map((user) => (
                    <button
                      type="button"
                      key={user.id}
                      onClick={() => {
                        void handleAddAdmin(user);
                      }}
                      disabled={addAdmin.isPending}
                      className="flex w-full flex-col items-start gap-0.5 border-b px-3 py-2 text-left text-sm hover:bg-muted disabled:opacity-50 last:border-b-0"
                    >
                      <span className="font-medium">{displayName(user)}</span>
                      {user.email && (
                        <span className="text-xs text-muted-foreground">
                          {user.email}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {admins.length === 0 ? (
        <div className="rounded border border-dashed p-8 text-center text-sm text-muted-foreground">
          No admins yet. Add one to manage this partner.
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Admin</TableHead>
              <TableHead>Role</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {admins.map((row) => {
              const isPendingThisRow =
                removeAdmin.isPending &&
                confirmRemoveTarget?.user_id === row.user_id;
              return (
                <TableRow key={row.user_id}>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">
                        {displayName(row.users)}
                      </span>
                      {row.users?.email && (
                        <span className="text-xs text-muted-foreground">
                          {row.users.email}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {row.is_workspace_owner ? (
                      <Badge variant="default">
                        <Crown className="mr-1 h-3 w-3" />
                        Workspace owner
                      </Badge>
                    ) : (
                      <Badge variant="secondary">Admin</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="destructive"
                      disabled={isPendingThisRow}
                      onClick={() => setConfirmRemoveTarget(row)}
                    >
                      {isPendingThisRow ? (
                        <>
                          <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                          Removing…
                        </>
                      ) : (
                        'Remove'
                      )}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <AlertDialog
        open={confirmRemoveTarget !== null}
        onOpenChange={(open) => {
          if (!open && !removeAdmin.isPending) {
            setConfirmRemoveTarget(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this admin?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmRemoveTarget && (
                <>
                  {displayName(confirmRemoveTarget.users)} will lose admin
                  access to this enterprise partner. This does not delete their
                  BuildIt account.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeAdmin.isPending}>
              Cancel
            </AlertDialogCancel>
            <Button
              onClick={handleConfirmRemove}
              disabled={removeAdmin.isPending}
              variant="destructive"
            >
              {removeAdmin.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Removing…
                </>
              ) : (
                'Remove admin'
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
